import { useCallback, useState } from "react";
import isBrowser from "../utils/isBrowser";

function readStorage<T>(key: string, fallback: T): T {
  if (!isBrowser) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeStorage<T>(key: string, value: T) {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / private browsing
  }
}

/**
 * useState that mirrors JSON into localStorage so remounts (e.g. list →
 * detail → back) restore the same value.
 */
function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => readStorage(key, defaultValue));

  const setPersisted = useCallback(
    (next: T | ((prev: T) => T)) => {
      setState(prev => {
        const value = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        writeStorage(key, value);
        return value;
      });
    },
    [key],
  );

  return [state, setPersisted];
}

export default useLocalStorageState;
